import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Pin, Pencil, Trash2, Share2 } from "lucide-react";
import type { Task, TaskStatus } from "@/lib/types";

interface Props {
  task: Task;
  projectMeta?: any;
  creator: string;

  onStatus: (t: Task, s: TaskStatus) => void;
  onPin: (id: string) => void;
  onEdit: (t: Task) => void;
  onDelete: (t: Task) => void;
  onShare?: (t: Task) => void;

  getStatusBadge: (s: TaskStatus) => string;
  getStatusLabel: (s: TaskStatus) => string;

  isPinned: boolean;
  onRowClick?: (t: Task) => void;
}

export default function TaskRow({
  task,
  projectMeta,
  creator,
  onStatus,
  onPin,
  onEdit,
  onDelete,
  onShare,
  getStatusBadge,
  getStatusLabel,
  onRowClick,
  isPinned,
}: Props) {
  const isOwner = task.access?.kind !== "shared";

    const daysLeft = getDaysUntilDue(task);
    const percent = getTimelinePercent(daysLeft);
    const timelineColor = getTimelineColor(daysLeft);

return (
                <tr
                onClick={(e) => {
                    const el = e.target as HTMLElement;
                    if (el.closest("button, select, input, [role='button']")) return;
                    onRowClick?.(task);
                }}
                className="
                border-b border-purple-500/10
                hover:bg-purple-700/15
                active:bg-purple-700/25
                cursor-pointer
                transition-colors
            "
            >

            {/* TASK */}
            <td className="px-3 py-2 text-left text-[12px] text-purple-100">
                {task.title}
            </td>

            {/* PROJECT (stacked) */}
            <td className="px-3 py-2 text-center leading-tight">
                {projectMeta ? (
                <div className="flex flex-col">
                    <span className="text-[10px] text-muted-foreground">
                    {[projectMeta.city, projectMeta.state].filter(Boolean).join(", ")}
                    </span>
                    <span className="text-[12px] text-purple-100">
                    {projectMeta.project_name}
                    </span>
                </div>
                ) : (
                "—"
                )}
            </td>

            {/* ASSIGNEE */}
            <td className="px-3 py-2 text-center text-[12px]">{creator}</td>

            {/* DUE */}
            <td className="px-3 py-2 text-[12px] text-center whitespace-nowrap">
                {task.due_date
                ? parseLocalDate(task.due_date)
                    .toLocaleDateString("en-US")
                    .replace(/\//g, "-")
                : "-"}
            </td>

            {/* TIMELINE */}
            <td className="px-3 py-2">
            <div className="h-2 w-full bg-purple-900/50 rounded">
                <div
                className="h-2 rounded transition-all"
                style={{ width: `${percent}%`, backgroundColor: timelineColor }}
                />
            </div>
            </td>



            {/* STATUS */}
            <td className="px-3 py-2 text-center">
                <Select
                value={task.status}
                onValueChange={(v) => onStatus(task, v as TaskStatus)}
                >
                <SelectTrigger
                onClick={(e) => e.stopPropagation()}
                className={`${getStatusBadge(task.status)} h-6 text-[12px] pr-1 pl-2`}
                >
                    <SelectValue />
                </SelectTrigger>

                <SelectContent>
                    {["pending", "in_progress", "coordinating", "complete"].map((s) => (
                    <SelectItem key={s} value={s}>
                        {getStatusLabel(s as TaskStatus)}
                    </SelectItem>
                    ))}
                </SelectContent>
                </Select>
            </td>

            {/* MANAGE */}
            <td className="px-2 py-2 align-middle">
                <div className="flex justify-center items-center gap-0.5">

                {isOwner && onShare && (
                    <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onShare(task);
                    }}
                    className="p-0.5 rounded hover:text-orange-800 transition"
                    >
                    <Share2 size={15}/>
                    </button>
                )}

                <button
                    type="button"
                    onClick={(e) => {
                    e.stopPropagation();
                    onPin(task.id);
                    }}
                    className={`p-0.5 rounded transition ${
                    isPinned
                        ? "text-yellow-400"
                        : "hover:text-yellow-300"
                    }`}
                >
                    <Pin size={15} />
                </button>

                <button
                    type="button"
                    onClick={(e) => {
                    e.stopPropagation();
                    onEdit(task);
                    }}
                    className="p-0.5 rounded hover:text-green-800 transition"
                >
                    <Pencil size={15} />
                </button>

        <button
            type="button"
            onClick={(e) => {
            e.stopPropagation();
            onDelete(task);
            }}
            className="p-0.5 rounded hover:text-red-800 transition"
        >
            <Trash2 size={15} />
        </button>
        </div>
    </td>
    </tr>
  );
}

function parseLocalDate(dateStr: string) {
  const clean = dateStr.split("T")[0]; // strip time + timezone
  const [year, month, day] = clean.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function getStartOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function getDaysUntilDue(task: Task) {
  if (!task.due_date) return null;

  const today = getStartOfToday();
  const due = parseLocalDate(task.due_date);

  return Math.round(
    (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
}

const MAX_DAYS = 14;

function getTimelinePercent(daysLeft: number | null) {
  if (daysLeft === null || Number.isNaN(daysLeft)) return 0;
  if (daysLeft < 0) return 100;
  if (daysLeft >= MAX_DAYS) return 0;

  return Math.round(((MAX_DAYS - daysLeft) / MAX_DAYS) * 100);
}

function getTimelineColor(daysLeft: number | null): string {
  if (daysLeft === null || Number.isNaN(daysLeft)) return "#a855f7";  // purple
  if (daysLeft < 0)  return "#ef4444";  // red — past due
  if (daysLeft <= 3) return "#f97316";  // orange — within 3 days
  if (daysLeft <= 7) return "#eab308";  // yellow — within 1 week
  return "#22c55e";                     // green — more than 1 week
}
